import React, { useEffect, useState } from 'react';
import {
  Alert, Autocomplete, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider,
  FormControl, FormControlLabel, Grid, InputLabel, MenuItem, Select, Switch, TextField, Typography,
} from '@mui/material';
import {
  IMPLANT_LOADING_PROTOCOLS,
  IMPLANT_PLATFORMS,
  IMPLANT_STATUSES,
} from '../../constants/odontology';
import type { DentalImplant } from '../../types/odontology';
import { createDentalImplant, updateDentalImplant } from '../../services/odontologyService';
import ToothSelect from './ToothSelect';
import { useDentalProfessionals } from './odontologyHooks';

export interface DentalImplantDialogProps {
  open: boolean;
  onClose: () => void;
  patientId: string;
  unitId?: string | null;
  implant?: DentalImplant | null;
  defaultToothNumber?: string | null;
  defaultProfessionalId?: string | null;
  onSaved: () => void;
}

interface FormState {
  tooth_number: string;
  brand: string;
  model: string;
  platform: string;
  diameter_mm: string;
  length_mm: string;
  lot_number: string;
  reference_code: string;
  surgery_date: string;
  torque_ncm: string;
  stability_isq: string;
  surgical_guide: boolean;
  bone_graft: string;
  membrane: string;
  sinus_lift: boolean;
  loading_protocol: string;
  healing_abutment: string;
  second_stage_date: string;
  prosthesis_type: string;
  prosthesis_date: string;
  status: string;
  professional_id: string;
  notes: string;
}

const emptyForm = (toothNumber: string, professionalId: string): FormState => ({
  tooth_number: toothNumber,
  brand: '',
  model: '',
  platform: '',
  diameter_mm: '',
  length_mm: '',
  lot_number: '',
  reference_code: '',
  surgery_date: '',
  torque_ncm: '',
  stability_isq: '',
  surgical_guide: false,
  bone_graft: '',
  membrane: '',
  sinus_lift: false,
  loading_protocol: '',
  healing_abutment: '',
  second_stage_date: '',
  prosthesis_type: '',
  prosthesis_date: '',
  status: 'planned',
  professional_id: professionalId,
  notes: '',
});

const numberOrNull = (value: string): number | null => {
  const parsed = Number(value.replace(',', '.'));
  return value.trim() && Number.isFinite(parsed) ? parsed : null;
};

const DentalImplantDialog: React.FC<DentalImplantDialogProps> = ({
  open,
  onClose,
  patientId,
  unitId,
  implant,
  defaultToothNumber,
  defaultProfessionalId,
  onSaved,
}) => {
  const [form, setForm] = useState<FormState>(emptyForm('', ''));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const professionals = useDentalProfessionals(open);

  useEffect(() => {
    if (!open) return;
    setError(null);
    if (implant) {
      setForm({
        tooth_number: implant.tooth_number || '',
        brand: implant.brand || '',
        model: implant.model || '',
        platform: implant.platform || '',
        diameter_mm: implant.diameter_mm != null ? String(implant.diameter_mm) : '',
        length_mm: implant.length_mm != null ? String(implant.length_mm) : '',
        lot_number: implant.lot_number || '',
        reference_code: implant.reference_code || '',
        surgery_date: implant.surgery_date?.slice(0, 10) || '',
        torque_ncm: implant.torque_ncm != null ? String(implant.torque_ncm) : '',
        stability_isq: implant.stability_isq != null ? String(implant.stability_isq) : '',
        surgical_guide: Boolean(implant.surgical_guide),
        bone_graft: implant.bone_graft || '',
        membrane: implant.membrane || '',
        sinus_lift: Boolean(implant.sinus_lift),
        loading_protocol: implant.loading_protocol || '',
        healing_abutment: implant.healing_abutment || '',
        second_stage_date: implant.second_stage_date?.slice(0, 10) || '',
        prosthesis_type: implant.prosthesis_type || '',
        prosthesis_date: implant.prosthesis_date?.slice(0, 10) || '',
        status: implant.status || 'planned',
        professional_id: implant.professional_id || '',
        notes: implant.notes || '',
      });
    } else {
      setForm(emptyForm(defaultToothNumber || '', defaultProfessionalId || ''));
    }
  }, [open, implant, defaultToothNumber, defaultProfessionalId]);

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    if (!form.tooth_number) {
      setError('Selecione a posição do implante.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        patient_id: patientId,
        unit_id: unitId || undefined,
        professional_id: form.professional_id || null,
        tooth_number: form.tooth_number,
        brand: form.brand.trim() || null,
        model: form.model.trim() || null,
        platform: form.platform.trim() || null,
        diameter_mm: numberOrNull(form.diameter_mm),
        length_mm: numberOrNull(form.length_mm),
        lot_number: form.lot_number.trim() || null,
        reference_code: form.reference_code.trim() || null,
        surgery_date: form.surgery_date || null,
        torque_ncm: numberOrNull(form.torque_ncm),
        stability_isq: numberOrNull(form.stability_isq),
        surgical_guide: form.surgical_guide,
        bone_graft: form.bone_graft.trim() || null,
        membrane: form.membrane.trim() || null,
        sinus_lift: form.sinus_lift,
        loading_protocol: (form.loading_protocol || null) as DentalImplant['loading_protocol'],
        healing_abutment: form.healing_abutment.trim() || null,
        second_stage_date: form.second_stage_date || null,
        prosthesis_type: form.prosthesis_type.trim() || null,
        prosthesis_date: form.prosthesis_date || null,
        status: form.status,
        notes: form.notes.trim() || null,
      };

      if (implant?.id) await updateDentalImplant(implant.id, payload);
      else await createDentalImplant(payload);

      onSaved();
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Erro ao salvar implante');
    } finally {
      setSaving(false);
    }
  };

  const sectionLabel = (text: string) => (
    <Grid item xs={12}>
      <Divider textAlign="left" sx={{ my: 0.5 }}>
        <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          {text}
        </Typography>
      </Divider>
    </Grid>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography component="span" sx={{ fontWeight: 700, fontSize: '1.05rem', color: '#0F172A' }}>
          {implant ? 'Editar implante' : 'Novo implante'}
        </Typography>
        <Typography variant="body2" sx={{ color: '#64748B', mt: 0.25 }}>
          Marca, lote e dimensões ficam registrados para rastreabilidade
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <ToothSelect
              value={form.tooth_number}
              onChange={(value) => setField('tooth_number', value)}
              label="Posição (FDI)"
              includeDeciduous={false}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel id="implant-status-label">Status</InputLabel>
              <Select
                labelId="implant-status-label"
                label="Status"
                value={form.status}
                onChange={(e) => setField('status', e.target.value)}
              >
                {IMPLANT_STATUSES.map((s) => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>

          {sectionLabel('Componente')}

          <Grid item xs={12} sm={6} md={3}>
            <TextField fullWidth size="small" label="Marca" value={form.brand}
              onChange={(e) => setField('brand', e.target.value)} placeholder="Ex.: Neodent" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField fullWidth size="small" label="Modelo" value={form.model}
              onChange={(e) => setField('model', e.target.value)} placeholder="Ex.: Drive CM" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              freeSolo
              size="small"
              options={IMPLANT_PLATFORMS}
              value={form.platform}
              onInputChange={(_, value) => setField('platform', value)}
              renderInput={(params) => <TextField {...params} label="Conexão" />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField fullWidth size="small" label="Lote" value={form.lot_number}
              onChange={(e) => setField('lot_number', e.target.value)} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField fullWidth size="small" label="Diâmetro (mm)" value={form.diameter_mm}
              onChange={(e) => setField('diameter_mm', e.target.value)} placeholder="3,75" />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField fullWidth size="small" label="Comprimento (mm)" value={form.length_mm}
              onChange={(e) => setField('length_mm', e.target.value)} placeholder="11,5" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="Código de referência" value={form.reference_code}
              onChange={(e) => setField('reference_code', e.target.value)} />
          </Grid>

          {sectionLabel('Cirurgia')}

          <Grid item xs={12} sm={6} md={3}>
            <TextField fullWidth size="small" type="date" label="Data da cirurgia" InputLabelProps={{ shrink: true }}
              value={form.surgery_date} onChange={(e) => setField('surgery_date', e.target.value)} />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <TextField fullWidth size="small" label="Torque (Ncm)" value={form.torque_ncm}
              onChange={(e) => setField('torque_ncm', e.target.value)} placeholder="35" />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <TextField fullWidth size="small" label="ISQ" value={form.stability_isq}
              onChange={(e) => setField('stability_isq', e.target.value)} placeholder="70" />
          </Grid>
          <Grid item xs={12} sm={6} md={5}>
            <FormControl fullWidth size="small">
              <InputLabel id="implant-loading-label">Protocolo de carga</InputLabel>
              <Select
                labelId="implant-loading-label"
                label="Protocolo de carga"
                value={form.loading_protocol}
                onChange={(e) => setField('loading_protocol', e.target.value)}
              >
                <MenuItem value="">Não definido</MenuItem>
                {IMPLANT_LOADING_PROTOCOLS.map((p) => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="Enxerto ósseo" value={form.bone_graft}
              onChange={(e) => setField('bone_graft', e.target.value)} placeholder="Ex.: Bio-Oss 0,5 g" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="Membrana" value={form.membrane}
              onChange={(e) => setField('membrane', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={<Switch checked={form.surgical_guide} onChange={(e) => setField('surgical_guide', e.target.checked)} />}
              label="Guia cirúrgico"
              sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={<Switch checked={form.sinus_lift} onChange={(e) => setField('sinus_lift', e.target.checked)} />}
              label="Levantamento de seio"
              sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
            />
          </Grid>

          {sectionLabel('Reabilitação')}

          <Grid item xs={12} sm={6} md={3}>
            <TextField fullWidth size="small" label="Cicatrizador" value={form.healing_abutment}
              onChange={(e) => setField('healing_abutment', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField fullWidth size="small" type="date" label="Reabertura" InputLabelProps={{ shrink: true }}
              value={form.second_stage_date} onChange={(e) => setField('second_stage_date', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField fullWidth size="small" label="Tipo de prótese" value={form.prosthesis_type}
              onChange={(e) => setField('prosthesis_type', e.target.value)} placeholder="Ex.: coroa parafusada" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField fullWidth size="small" type="date" label="Instalação da prótese" InputLabelProps={{ shrink: true }}
              value={form.prosthesis_date} onChange={(e) => setField('prosthesis_date', e.target.value)} />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel id="implant-professional-label">Profissional</InputLabel>
              <Select
                labelId="implant-professional-label"
                label="Profissional"
                value={form.professional_id}
                onChange={(e) => setField('professional_id', e.target.value)}
              >
                <MenuItem value="">Não informado</MenuItem>
                {professionals.map((p) => <MenuItem key={p.id} value={p.id}>{p.full_name}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <TextField fullWidth size="small" multiline minRows={2} label="Observações" value={form.notes}
              onChange={(e) => setField('notes', e.target.value)} />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', color: '#64748B' }}>Cancelar</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          sx={{ textTransform: 'none', bgcolor: '#0D9488', '&:hover': { bgcolor: '#0F766E' } }}
        >
          {saving ? 'Salvando…' : 'Salvar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DentalImplantDialog;
